import ArticleLayout from '../../components/ArticleLayout';
import UdluftningFigure from '../../components/figures/UdluftningFigure';

export default function UdluftningAfRadiator() {
  return (
    <ArticleLayout
      title="Sådan lufter du en radiator ud: Rækkefølge, nøgle og vandtryk"
      metaTitle="Udluftning af radiator: Guide, nøgle og rækkefølge"
      metaDescription="Lær at udlufte radiatoren korrekt. Se den optimale rækkefølge i hus og etageejendom, brug af radiatornøgle og efterfyldning af anlægstryk."
      canonicalPath="/udluftning-af-radiator"
      introLead="At udlufte en radiator fjerner opsamlede luftlommer, så det varme vand frit kan fylde hele radiatorens overflade og opvarme rummet uden rislende lyde."
      introAnswer="Processen kræver blot en standard firkantet radiatornøgle, en klud og en lille skål. For at opnå det bedste resultat skal cirkulationspumpen slukkes 15–20 minutter forinden, hvorefter du åbner luftskruen roligt, indtil der siver en jævn vandstråle ud."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Udluftning trin for trin' },
        { id: 'raekkefoelge', title: '2. Den rigtige rækkefølge i hus og lejlighed' },
        { id: 'noegle', title: '3. Radiatornøglen og alternativer' },
        { id: 'efterfyldning', title: '4. Tjek anlægstrykket bagefter' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 6 trin til en fejlfri udluftning"
      checklist={[
        {
          title: 'Sluk cirkulationspumpen 15–20 minutter før start',
          description: 'Når vandstrømmen standser, skilles luftboblerne fra vandet og stiger helt til vejrs i anlægget.',
        },
        {
          title: 'Åbn alle termostater på fuld styrke (trin 5)',
          description: 'Dette sikrer fri passage inde i radiatoren, så luften uhindret kan presses mod luftskruen.',
        },
        {
          title: 'Sæt radiatornøglen på luftskruen i toppen',
          description: 'Luftskruen er placeret i modsat ende af termostaten øverst på radiatorens gavl.',
        },
        {
          title: 'Hold klud og beholder tæt under skruens udløbstud',
          description: 'Udløbstuden kan pege fremad eller skråt nedad; beskyt væg og gulv mod sort radiatorvand.',
        },
        {
          title: 'Drej nøglen en halv omgang mod uret',
          description: 'Lyt til den hvislende luft, og hold skruen åben, indtil der kommer en rolig, ubrudt vandstråle.',
        },
        {
          title: 'Luk skruen fast til igen uden at overspænde gevindet',
          description: 'Spænd med to fingres kraft, tør efter med kluden, og tænd cirkulationspumpen igen.',
        },
      ]}
      diagram={<UdluftningFigure />}
      sections={[
        {
          id: 'raekkefoelge',
          title: 'Den optimale rækkefølge i hus og etageejendom',
          content: (
            <>
              <p>
                Rækkefølgen afhænger af, om du bor i et enfamiliehus eller i en etageejendom:
              </p>
              <p>
                <strong>I et to- eller treetagers hus:</strong> Sluk cirkulationspumpen. Begynd altid med radiatorerne i stueetagen eller kælderen tættest på fyret, og arbejd dig opad mod førstesal og kvistværelser. Da luft stiger op, samler de største mængder sig i husets øverste radiatorer. Ved at tage dem til sidst evakuerer du luften mest effektivt.
              </p>
              <p>
                <strong>I en lejlighed i etageejendom:</strong> Bor du i stuen eller på 1. sal, er der sjældent megen luft i dine radiatorer. Bor du derimod på øverste etage, samles luften fra hele ejendommens lodrette streng hos dig. Her er udluftning afgørende for hele opgangens varmekomfort.
              </p>
            </>
          ),
        },
        {
          id: 'noegle',
          title: 'Radiatornøglen: 5 mm standard eller skruetrækker',
          content: (
            <>
              <p>
                De fleste radiatorer i Danmark anvender en lille firkantet nøgle med <span className="font-mono">5 mm</span> indvendigt mål (ofte udført i messing eller støbt zink). Nøglen kan købes i ethvert byggemarked for 20–40 kr.
              </p>
              <p>
                Nogle nyere luftskruer har en kombineret kærv, som også tillader brug af en almindelig ligekærvet skruetrækker. Brug aldrig en fladtang eller rørtang på den lille firkantede tap, da metallet er blødt og let slides rundt, så skruen bliver umulig at betjene.
              </p>
            </>
          ),
        },
        {
          id: 'efterfyldning',
          title: 'Kontrollér altid vandtrykket efter endt udluftning',
          content: (
            <>
              <p>
                Når der lukkes luft og vand ud af systemet, falder det samlede tryk på centralvarmeanlægget.
              </p>
              <p>
                Gå ned til varmecentralen eller gaskedlen og aflæs manometeret. I et almindeligt parcelhus skal nålen stå på typisk <span className="font-mono">1,5 bar</span> (eller det grønne felt). Hvis trykket er faldet under <span className="font-mono">1,0 bar</span>, skal der fyldes vand på via påfyldningshanen, indtil korrekt driftstryk er genoprettet.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal du stoppe og ringe til en fagmand?',
        content: (
          <p>
            Hvis luftskruen sidder urokkeligt fast af rust, eller hvis skruens hoved knækker, må du aldrig bore eller tvinge den med hårdt værktøj under vandtryk. En knækket luftskrue kan skyde ud og forårsage en akut oversvømmelse med skoldhedt vand. Kontakt en VVS-installatør, som kan fryse røret eller tappe anlægget forsvarligt.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Hvor meget vand skal der komme ud ved udluftning?',
          a: 'Kun et par deciliter eller hvad der svarer til en rolig, ubrudt stråle i 2–3 sekunder efter at den hvislende luft er ophørt. Tøm aldrig hele radiatoren for vand.',
        },
        {
          q: 'Hvorfor er radiatorvandet mørkt eller ildelugtende?',
          a: 'Radiatorvand i lukkede anlæg er iltfrit og indeholder mikroskopiske jernoxidpartikler. Den mørke farve og svage metalliske lugt er helt normal og betyder ikke, at anlægget er ødelagt.',
        },
        {
          q: 'Hvorfor skal pumpen slukkes før udluftning?',
          a: 'Når pumpen kører, pisker den luften rundt i vandet som mikroskopiske bobler. Når pumpen er slukket i 15 minutter, samler boblerne sig til én stor luftlomme øverst ved ventilen.',
        },
        {
          q: 'Hvad gør jeg, hvis jeg bor i lejebolig på øverste etage?',
          a: 'I lejeboliger kan du udlufte radiatoren med nøgle. Skal der fyldes vand på varmecentralen bagefter for at genskabe trykket, skal du give besked til viceværten.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-luftskrue-sidder-fast', text: 'Luftskruen sidder fast', desc: 'hvad man må prøve, og hvad man skal lade være med' },
        { to: '/radiator-tryk', text: 'Vandtrykket i varmeanlægget', desc: 'kontrol af manometer og korrekt driftstryk' },
        { to: '/radiator-varmer-ikke-efter-udluftning', text: 'Stadig kold efter udluftning', desc: 'de næste 3 ting at tjekke' },
        { to: '/radiator-lyder-som-vand', text: 'Radiatoren risler eller klukker', desc: 'typiske tegn på luft' },
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'den store tjekliste' },
        { to: '/radiatornoegle', text: 'Radiatornøgle: Hvilken nøgle passer?', desc: 'luftskruetyper og det rigtige værktøj' },
      ]}
      sources={[
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/saadan-faar-du-mest-ud-af-fjernvarmen', label: 'Sådan får du mest ud af fjernvarme | SparEnergi' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/sommerluk-varmeanlaegget', label: 'Sommerluk varmeanlægget og spar 500 kr. på 5 min | SparEnergi' },
      ]}
    />
  );
}
